/**
 * Login Modal Component
 *
 * Modal-based authentication interface that replaces the full-page login flow.
 * Preserves user context by showing login form as an overlay.
 *
 * Architecture: Uses Layer 2 semantic tokens (--oro-*, --text-*) only.
 * Pattern: Follows existing modal patterns from AdminInviteModal.tsx
 *
 * Issue #830: Redirect URL stored in D1 (not sessionStorage) for cross-platform
 * compatibility (React Native/Expo). Magic link opens in new tab/browser, so
 * sessionStorage (tab-scoped) doesn't work.
 *
 * Issue #1328: SMS login support with auto-detect email vs phone.
 *
 * Terms consent (#3262, Apple Guideline 1.2 via #3257 F4): the line under the
 * form links Terms and Privacy and PRESENTS the agreement before sign-in, in
 * every non-loading sub-state (idle, SMS code, reviewer). Presented only —
 * the recorded-acceptance artifact remains #2996 (blocked by #2928); do not
 * read this line as satisfying it.
 *
 * NOTE (#2853): this component still does NOT render a TCR consent block. The
 * compliant block is `SmsConsentDisclosure`, whose only consumer is
 * `AdminInviteModal` — i.e. it appears when a leader types someone ELSE's
 * number, never when a user types their own here. Closing that gap is tracked
 * on epic #2936; the #3262 terms line is an EULA presentation, not TCR
 * consent, and does not close it.
 */

import { useState, useEffect, useCallback, useRef, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import Button from './Button';
import Input from './Input';
import Card from './Card';
import { authApi, type ApiError } from '../lib/api';
import { invitesApi } from '../lib/api/invites';
import { useAuth } from '../contexts/AuthContext';
import { getUserFacingError } from '../lib/errorMessages';
import { smsOptedOutRecoveryCopy } from '../lib/smsRecovery';
import { normalizePhoneE164 } from '../lib/phone';
import EnvironmentBanner from './EnvironmentBanner';
import OroLockup from './OroLockup';

// STOP-locked recovery copy (#2930) now lives in `../lib/smsRecovery` so web and
// native share one LEAD + ™ cadence. This component consumes `smsOptedOutRecoveryCopy`
// (imported above); the `SMS_OPTED_OUT_RECOVERY_LEAD` constant is asserted directly
// from that shared module in the tests.

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  pendingNavigation?: string | null;
}

// Detect if input looks like a phone number
// Matches: +1234567890, 123-456-7890, (123) 456-7890, etc.
function isPhoneNumber(value: string): boolean {
  const cleaned = value.replace(/[\s\-().]/g, '');
  // Must start with + or digit, and have 10-15 digits total
  return /^\+?\d{10,15}$/.test(cleaned);
}


export default function LoginModal({
  isOpen,
  onClose,
  onSuccess,
  pendingNavigation
}: LoginModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [identifier, setIdentifier] = useState(''); // email or phone
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);
  // SMS verification (#1453)
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [normalizedPhone, setNormalizedPhone] = useState(''); // E.164 for API calls
  // #2345: App Review demo-account static-code entry (email path only; shown
  // when /auth/magic-link/request returns reviewer_code_entry for this email).
  const [reviewerMode, setReviewerMode] = useState(false);
  const [reviewerCode, setReviewerCode] = useState('');

  // #3264 Codex r3 P2: auth-attempt generation, bumped by the close-reset
  // effect. Every async handler captures the current value before its await
  // and re-checks it before any setState in the continuation, so a response
  // that resolves AFTER the modal closed is INERT — nothing restores
  // awaitingCode/success/error onto the reset (or reopened) modal. This is
  // the "close-during-pending is safe" axis: the consent links keep their
  // raw, immediate onClose (blocking on `loading` would strand the overlay
  // over the destination page — George r2), and the late response is simply
  // ignored instead. A ref, not state: the check must be synchronous inside
  // the continuation, immune to the closure staleness setState guards have.
  //
  // #3264 Codex r4 P2 boundary: the guard gates UI-STATE transitions ONLY
  // (navigate, onClose/onSuccess, awaitingCode/success/loading/error). It
  // must NOT gate query-cache writes that record SERVER FACTS — once
  // verify/reviewer-login resolves, the session cookie exists whether or not
  // the modal is still open, so setQueryData(['me'], …) + the auth
  // invalidations run unconditionally on success. Guarding them left a
  // cached guest ['me'] (5-min staleTime) masking a real session when the
  // user closed via a legal link mid-verify.
  const attemptRef = useRef(0);

  // Get Turnstile site key from environment (optional - graceful degradation)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  // Detect if user is entering a phone number
  const isPhone = isPhoneNumber(identifier);

  const handleAuthSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    }
    
    // Navigate to pending destination or close modal
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    
    onClose();
  }, [onSuccess, pendingNavigation, navigate, onClose]);

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  // Close modal if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      handleAuthSuccess();
    }
  }, [isAuthenticated, isOpen, handleAuthSuccess]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Invalidate in-flight requests FIRST (see attemptRef above), then
      // reset. loading is reset here too — its finally-side reset is
      // generation-guarded, so without this line a close mid-request would
      // leave the reopened modal stuck disabled.
      attemptRef.current += 1;
      setLoading(false);
      setIdentifier('');
      setError(null);
      setSuccess(false);
      setTurnstileToken(null);
      setTurnstileError(false);
      setAwaitingCode(false);
      setVerificationCode('');
      setCanResend(false);
      setNormalizedPhone('');
      setReviewerMode(false);
      setReviewerCode('');
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, handleClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (!identifier.trim()) {
      setError('Please enter an email address or phone number');
      return;
    }

    // Normalize phone to E.164 upfront — used for all SMS API calls
    let phoneE164 = '';
    if (isPhone) {
      const normalized = normalizePhoneE164(identifier);
      if (!normalized) {
        setError('Please enter a valid US or Canada phone number');
        return;
      }
      phoneE164 = normalized;
      setNormalizedPhone(normalized); // Persist for verify/resend
    } else {
      // Email validation
      if (!identifier.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    // #3264 Codex r3 P2: snapshot the generation; a close bumps it.
    const attempt = attemptRef.current;

    try {
      // Extract invite code from pendingNavigation if it's an invite URL
      let inviteCode: string | undefined;
      if (pendingNavigation?.startsWith('/invite/')) {
        inviteCode = pendingNavigation.split('/invite/')[1]?.split('?')[0];
      }

      if (isPhone) {
        // SMS login flow (#1453) — send verification code via TextGrid
        await authApi.requestSmsCode(
          phoneE164,
          turnstileToken || undefined,
          inviteCode,
          pendingNavigation || undefined
        );
        // Modal closed while the request was in flight — the close-reset
        // already cleared normalizedPhone; setting awaitingCode here would
        // reopen onto a verification form that submits an empty phone.
        if (attemptRef.current !== attempt) return;
        // Show code input instead of generic success
        setAwaitingCode(true);
        setLoading(false);
        // Enable resend after 30 seconds
        setTimeout(() => {
          if (attemptRef.current === attempt) setCanResend(true);
        }, 30000);
        return; // Don't set success (that shows "check your inbox" message)
      } else {
        // Email magic link flow
        const resp = await authApi.requestMagicLink(
          identifier,
          turnstileToken || undefined,
          inviteCode,
          pendingNavigation || undefined
        );
        // Closed mid-flight: don't restore a stale success/reviewer state.
        if (attemptRef.current !== attempt) return;
        // #2345: armed App Review demo account — collect the static review code
        // instead of "check your email" (the reviewer can't read our inbox).
        if (resp?.reviewer_code_entry) {
          setReviewerMode(true);
          setLoading(false);
          return; // Don't set success (that shows "check your inbox")
        }
      }
      setSuccess(true);
      // Reset Turnstile widget after successful submission
      setTurnstileToken(null);
    } catch (err) {
      if (attemptRef.current !== attempt) return; // closed mid-flight (#3264 P2)
      const apiError = err as ApiError;

      // Handle bot detection error
      if (apiError.code === 'BOT_CHECK_FAILED') {
        setError('Security check failed. Please refresh the page and try again.');
        setTurnstileError(true);
        setLoading(false);
        return;
      }

      // Handle environment whitelist restriction (#1447) — pass through backend message
      if (apiError.code === 'FORBIDDEN') {
        setError(apiError.message || 'This is a test environment. To sign up for ORO move\u2122, visit oro.fit');
        setLoading(false);
        return;
      }

      // #2930: a STOP-suppressed phone cannot receive a login code, so the
      // generic wrapper copy is a dead end. Lead with the shipped add-email
      // recovery path (reachable from a live session) and KEEP the backend's
      // own message, which carries the actionable "reply START to <number>"
      // target — the phone-only user's only self-serve door.
      if (apiError.code === 'SMS_OPTED_OUT') {
        setError(smsOptedOutRecoveryCopy(apiError));
        setLoading(false);
        return;
      }

      // Handle invite-required error - auto-submit to waitlist (email only)
      if (apiError.code === 'INVITE_REQUIRED' && !isPhone) {
        try {
          await invitesApi.joinWaitlist({
            email: identifier,
            source: 'blocked_signup',
          });
          if (attemptRef.current !== attempt) return; // closed mid-waitlist
          setSuccess(true); // Show same success message
        } catch {
          if (attemptRef.current !== attempt) return;
          setError('Unable to join waitlist. Please try again.');
        } finally {
          if (attemptRef.current === attempt) setLoading(false);
        }
        return;
      }

      setError(getUserFacingError(apiError.code, apiError.message));
    } finally {
      // Guarded: the close-reset already set loading=false for the next open;
      // an unguarded write here would be harmless today but is kept symmetric
      // with every other continuation write.
      if (attemptRef.current === attempt) setLoading(false);
    }
  };

  // SMS code verification handler (#1453)
  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    const attempt = attemptRef.current; // #3264 P2

    try {
      const response = await authApi.verifySmsCode(normalizedPhone, verificationCode);

      // Set user data directly in cache (MagicLoginPage pattern).
      // UNCONDITIONALLY — no generation check first (#3264 Codex r4 P2): the
      // session cookie is already set server-side, so these writes record a
      // server fact. A close mid-verify must not skip them, or the cached
      // guest ['me'] (5-min staleTime) keeps AuthContext signed-out over a
      // real session (the legal routes disable useMe, so nothing refetches).
      queryClient.setQueryData(['me'], response.user);
      await queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'none' });
      await queryClient.invalidateQueries({ queryKey: ['event'], refetchType: 'none' });

      // Closed anywhere between submit and here (#3264 P2 + George r2 F2):
      // login succeeded and the cache is seeded — skip the UI tail
      // (navigate/onSuccess/onClose) so a user who clicked a legal link
      // isn't yanked off it; AuthContext converges on its own.
      if (attemptRef.current !== attempt) return;

      // Navigate to redirect or pending destination
      if (response.redirect_url) {
        navigate(response.redirect_url);
      } else if (pendingNavigation) {
        navigate(pendingNavigation);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      if (attemptRef.current !== attempt) return; // closed mid-flight (#3264 P2)
      const apiError = err as ApiError;
      setError(apiError.message || 'Invalid code. Please try again.');
    } finally {
      if (attemptRef.current === attempt) setLoading(false);
    }
  };

  // #2345: App Review demo-account static-code verify. Mirrors handleVerifyCode's
  // post-login flow (cache seed + navigate); the backend gate does the real work.
  const handleReviewerLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!reviewerCode.trim()) {
      setError('Please enter the review code');
      return;
    }

    setLoading(true);
    setError(null);

    const attempt = attemptRef.current; // #3264 P2

    try {
      const response = await authApi.reviewerLogin(identifier, reviewerCode.trim());

      // Server-fact cache writes run unguarded (#3264 Codex r4 P2) — see
      // handleVerifyCode: the cookie is set even if the modal closed.
      queryClient.setQueryData(['me'], response.user);
      await queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'none' });
      await queryClient.invalidateQueries({ queryKey: ['event'], refetchType: 'none' });

      // Closed anywhere since submit (#3264 P2 + George r2 F2) — see
      // handleVerifyCode: cache is seeded, skip the navigate/close tail.
      if (attemptRef.current !== attempt) return;

      if (response.redirect_url) {
        navigate(response.redirect_url);
      } else if (pendingNavigation) {
        navigate(pendingNavigation);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      if (attemptRef.current !== attempt) return; // closed mid-flight (#3264 P2)
      const apiError = err as ApiError;
      setError(apiError.message || 'Invalid code. Please try again.');
    } finally {
      if (attemptRef.current === attempt) setLoading(false);
    }
  };

  // Resend SMS code — must pass same invite/redirect context as initial request
  const handleResendCode = async () => {
    setCanResend(false);
    setError(null);
    const attempt = attemptRef.current; // #3264 P2
    try {
      let inviteCode: string | undefined;
      if (pendingNavigation?.startsWith('/invite/')) {
        inviteCode = pendingNavigation.split('/invite/')[1]?.split('?')[0];
      }
      await authApi.requestSmsCode(
        normalizedPhone,
        turnstileToken || undefined,
        inviteCode,
        pendingNavigation || undefined
      );
      setTimeout(() => {
        if (attemptRef.current === attempt) setCanResend(true);
      }, 30000);
    } catch (err) {
      if (attemptRef.current !== attempt) return; // closed mid-flight (#3264 P2)
      // #2930: a user can opt out (reply STOP) after the first code, then tap
      // Resend and hit the same dead end. Map SMS_OPTED_OUT to the same
      // recovery copy as the request path; otherwise the generic resend error.
      const apiError = err as ApiError;
      setError(
        apiError.code === 'SMS_OPTED_OUT'
          ? smsOptedOutRecoveryCopy(apiError)
          : 'Failed to resend code. Please try again.',
      );
      setCanResend(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      // #2554: `aria-label`, not `aria-labelledby` — the visible "Login"
      // heading is gone (see the header below). APG's rule is labelledby when
      // a visible title exists, label when one does not. A `sr-only` h2 was
      // the alternative, rejected because its "not visible" half is unprovable
      // under jsdom: no Tailwind stylesheet loads there, so `sr-only` computes
      // to visible and the guard would pass whether or not the class survived.
      aria-label="Login"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm oro-fade-in"
        onClick={handleClose}
        aria-label="Close modal"
      />
      
      {/* Modal Content */}
      {/* The testid is the modal PANEL, deliberately — role="dialog" sits on the
          fixed inset-0 wrapper above, whose box is the whole viewport, so it is
          useless as a containment boundary in a layout test. The Card inside
          this div carries overflow-hidden and does the actual clipping, and it
          is w-full inside this fixed-width panel, so this box is the clipping
          box. Card takes no arbitrary props (see Card.tsx: children/variant/
          className only), so the handle goes here rather than on it. */}
      <div
        className="relative z-10 w-full max-w-sm oro-modal-enter"
        data-testid="login-modal-panel"
      >
        <Card className="border-oro-border shadow-xl shadow-black/50 bg-oro-surface overflow-hidden">
          {/* Environment warning for QA/Dev (#1448) — renders nothing on production */}
          <EnvironmentBanner className="-mx-4 md:-mx-5 -mt-4 md:-mt-5 mb-4" />
          {/* Header */}
          {/* #2553: the close button is positioned out of flow, NOT a flex
              sibling. As a sibling it forced `flex-1 mr-8` on the content
              column, so that column's centre sat (mr-8 + w-8) / 2 = 32px LEFT
              of the modal's — and `text-center` then faithfully centred the
              lockup, heading and subtitle on the wrong axis. The tell was that
              the subtitle shared the offset; a logo-only artwork problem would
              not have moved the text with it. Out of flow, the column spans the
              full width and the two axes coincide. */}
          <div className="relative mb-6">
            {/* px-10 reserves the close button's 32px on BOTH sides (40px, so
                8px of breathing room). Symmetric is the whole point: it keeps
                the column's centre on the modal's — the asymmetric `mr-8` is
                what caused #2553 — while still bounding the lockup so the
                out-of-flow button cannot land on the artwork.
                Needed because the button no longer reserves width: at the
                320px floor of the supported range (release-checklist.md:313,
                responsive-design/SKILL.md:80) the content box is 256px, and a
                centred 200px lockup left 28px gutters against a 32px button —
                a measured 4.9px overlap. Tailwind preflight's
                `img { max-width: 100% }` then lets the artwork shrink into the
                padded box instead of colliding. Caught by Frank on #2555;
                the e2e guard now runs the full 320-1280px range. */}
            <div className="text-center px-10">
              {/* #2523: horizontal, matching the native login screen (#2516).
                  No `rounded-xl` — the horizontal asset is trimmed to its ink
                  and transparent, so there is no plate to round. `size` is the
                  WIDTH here; height follows the artwork aspect. */}
              <OroLockup orientation="horizontal" size={200} className="mx-auto mb-3" />
              {/* #2538: no "ACCESS" eyebrow here. It predated the brand (03d1beca,
                  2025-12-02) and never fit the convention it looked like: ORO's
                  eyebrow is `text-xs tracking-[0.35em]` carrying a CONTEXTUAL
                  label (ClubMembersPage renders the club name in it). This one
                  was `text-sm tracking-[0.3em]` with the static word "Access" —
                  wrong metrics, and naming nothing.
                  #2554: the "Login" h2 that sat here is gone too — brand-owner
                  call, it said nothing the subtitle below does not, and it
                  separated the lockup from the instruction it belongs with.
                  Native's login (mobile/app/(auth)/login.tsx) already goes
                  lockup → content with nothing between, so this closes a
                  divergence. The dialog's accessible name moved to `aria-label`
                  on the wrapper above — do NOT delete that without replacing
                  it, or the dialog goes nameless. */}
              <p className="text-text-secondary">
                Enter your email or phone number to sign in.
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors rounded-lg hover:bg-oro-surface-alt"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Success State */}
          {success && (
            <div className="mb-6 rounded-md border border-oro-success/40 bg-oro-success/10 p-4">
              <p className="text-sm text-oro-success text-center">
                {isPhone
                  ? 'Check your phone! We sent you a verification code.'
                  : 'Check your inbox! We sent you a magic link.'}
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mb-6 rounded-md border border-oro-error/40 bg-oro-error/10 p-4">
              <p className="text-sm text-oro-error text-center">
                {error}
              </p>
            </div>
          )}

          {/* Auth Loading State */}
          {authLoading && (
            <div className="mb-6 p-4 text-center">
              <div className="oro-loading h-8 w-8 rounded-full bg-oro-gold mx-auto mb-2"></div>
              <p className="text-text-secondary text-sm">Checking authentication...</p>
            </div>
          )}

          {/* Login Form */}
          {!authLoading && (<>
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="identifier"
                  className="block text-sm font-medium tracking-wide text-text-secondary"
                >
                  {isPhone ? 'Phone number' : 'Email or phone'}
                </label>
                <Input
                  type={isPhone ? 'tel' : 'text'}
                  id="identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  disabled={loading || success || reviewerMode}
                  placeholder="you@example.com or (555) 123-4567"
                  required
                  size="lg"
                  autoComplete={isPhone ? 'tel' : 'email'}
                />
                {isPhone && !awaitingCode && (
                  <p className="text-xs text-text-muted">
                    SMS login available for US and Canada numbers.
                  </p>
                )}
              </div>

              {/* Cloudflare Turnstile Bot Protection */}
              {turnstileSiteKey && !success && !reviewerMode && (
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setTurnstileError(false);
                    }}
                    onError={() => {
                      setTurnstileError(true);
                      setTurnstileToken(null);
                    }}
                    onExpire={() => {
                      setTurnstileToken(null);
                    }}
                    options={{
                      theme: 'dark',
                      size: 'normal',
                      retry: 'auto',
                      appearance: 'always',
                    }}
                  />
                </div>
              )}
              
              {/* Turnstile Error Message */}
              {turnstileError && (
                <div className="rounded-md border border-oro-border bg-oro-surface-alt p-3">
                  <p className="text-xs text-text-secondary text-center">
                    Security verification failed. Please try refreshing the page.
                  </p>
                </div>
              )}
              
              {!awaitingCode && !reviewerMode && (
                <Button
                  type="submit"
                  disabled={loading || success}
                  loading={loading}
                  className="w-full"
                  size="lg"
                >
                  {isPhone ? 'Send Verification Code' : 'Send Login Link'}
                </Button>
              )}
            </form>

            {/* #3262: EULA presented before sign-in (Apple 1.2, #3257 F4).
                Inside the non-loading block but outside every sub-state
                conditional, so it survives the SMS-code and reviewer swaps.
                Styled per the SmsConsentDisclosure precedent (text-[11px]
                text-text-muted, react-router Link + underline). The links
                close the modal on click (Codex r1 F1): the Link handles the
                route, and without onClose the z-50 overlay stays parked over
                the destination page. Plain onClose, not handleClose — the
                navigation happens regardless, so the loading guard would
                only strand the overlay. Closing mid-request is SAFE because
                the attemptRef generation guard makes the late response inert
                (#3264 Codex r3 P2). */}
            <p
              className="mt-4 text-[11px] leading-relaxed text-text-muted text-center"
              data-testid="login-terms-consent"
            >
              By continuing, you agree to the{' '}
              <Link
                to="/terms"
                onClick={onClose}
                className="underline hover:text-text-secondary transition-colors"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                onClick={onClose}
                className="underline hover:text-text-secondary transition-colors"
              >
                Privacy Policy
              </Link>
              .
            </p>

            {/* SMS Code Verification (#1453) */}
            {awaitingCode && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <p className="text-sm text-text-secondary text-center">
                  Enter the 6-digit code sent to your phone.
                </p>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  disabled={loading}
                  placeholder="000000"
                  autoFocus
                  size="lg"
                  className="text-center tracking-[0.5em] text-xl"
                />
                <Button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  loading={loading}
                  className="w-full"
                  size="lg"
                >
                  Verify
                </Button>
                {canResend && (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="w-full text-sm text-text-muted hover:text-accent transition-colors"
                  >
                    Resend code
                  </button>
                )}
              </form>
            )}

            {/* #2345: App Review demo-account static-code entry (email path) */}
            {reviewerMode && (
              <form onSubmit={handleReviewerLogin} className="space-y-4">
                <p className="text-sm text-text-secondary text-center">
                  Enter the review access code for this account.
                </p>
                <Input
                  type="text"
                  value={reviewerCode}
                  onChange={(e) => setReviewerCode(e.target.value)}
                  disabled={loading}
                  placeholder="Review code"
                  autoFocus
                  size="lg"
                  maxLength={256}
                  autoComplete="off"
                />
                <Button
                  type="submit"
                  disabled={loading || !reviewerCode.trim()}
                  loading={loading}
                  className="w-full"
                  size="lg"
                >
                  Sign In
                </Button>
              </form>
            )}
          </>)}
        </Card>
      </div>
    </div>
  );
}